import { Menu } from '@mui/material'
import React, { ComponentType, createContext, Ref, useContext, useRef, useState } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { recordAuthorization } from '@omnea-app/fe-authz/accessRules'
import { DELETE_REQUEST, REJECT_REQUEST, REOPEN_REQUEST, TOGGLE_REQUEST_HIDDEN_STEPS } from '@omnea-app/fe-authz/recordRules'
import { nonViewOnly } from '@omnea-app/fe-authz/roleAuthorization'
import { useIsPermitted } from '@omnea-app/fe-authz/useIsPermitted'
import { RequestTypeEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { RequestDetail } from '@omnea-app/requests/requestsFrontendClient'
import { useRequestDetailContext } from '../../context/RequestDetailContext'
import { useIsAttachedToRequest, RequestParticipants } from '../../helpers/useIsAttachedToRequest'
import { AuditLogMenuItem } from './buttons/audit-log'
import { ConfidentialMenuItem } from './buttons/confidential'
import { DeleteMenuItem } from './buttons/delete'
import { DuplicateRequestMenuItem } from './buttons/duplicateRequest'
import { MuteMenuItem } from './buttons/mute'
import { PauseMenuItem } from './buttons/pause'
import { RejectMenuItem } from './buttons/reject'
import { ReopenMenuItem } from './buttons/reopen'
import { ResumeMenuItem } from './buttons/resume'
import { ToggleHiddenStepsMenuItem } from './buttons/toggle-hidden-steps'

const ManageMenuContext = createContext<{ closeMenu: () => void } | null>(null)

export const useManageMenuClose = () => {
  const context = useContext(ManageMenuContext)
  return context?.closeMenu
}

type Origin = {
  horizontal: 'center' | 'left' | 'right'
  vertical: 'center' | 'top' | 'bottom'
}

// Anchors must not be defined inline in renders to prevent the menu changing position
export type ManageMenuAnchor = ComponentType<{ buttonRef: Ref<HTMLButtonElement>; openMenu: () => void }>

// This can be RequestDetail or RequestHeaderData, so just the required properties are defined here
export type ManageMenuRequest = {
  rawData: {
    completedAt: string | null
    state: string | null
    type: string
    name?: string
    requestorUserId: string
    // Optional because RequestHeaderData callers may render before the detail response arrives;
    // the confidentiality item hides itself while it is unknown.
    isConfidential?: boolean
  }
  derivedFields: {
    ownerId: string | null
    currentUserAssignees: Pick<RequestDetail['derivedFields']['currentUserAssignees'][number], 'id'>[]
    currentGroupAssignees: RequestDetail['derivedFields']['currentGroupAssignees']
  }
} & RequestParticipants

type Props = {
  requestId: string
  request?: ManageMenuRequest
  onRejectRequest: (reason: string) => void
  onUnrejectRequest: (reason: string, productId?: string) => void
  onDeleteRequest: () => void
  anchor: ManageMenuAnchor
  anchorOrigin?: Origin
  transformOrigin?: Origin
}

export const ManageMenu = ({
  requestId,
  request,
  onDeleteRequest,
  onRejectRequest,
  onUnrejectRequest,
  anchor: Anchor,
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'right',
  },
  transformOrigin = {
    vertical: 'top',
    horizontal: 'right',
  },
}: Props) => {
  const { user } = useAuthenticatedUser()
  const { showHiddenSteps, toggleHiddenSteps } = useRequestDetailContext()
  const isAttachedToRequest = useIsAttachedToRequest(request)

  // Pause and resume read the one verb, so they answer alike; the buttons differ by the request's state.
  const canPause = useIsPermitted(
    { authorization: recordAuthorization('request', 'canPause', (context) => nonViewOnly(context) || isAttachedToRequest) },
    requestId
  )
  const canReject = useIsPermitted(REJECT_REQUEST, requestId)
  const canManageWorkflow = useIsPermitted(REOPEN_REQUEST, requestId)
  const showDeleteButton = useIsPermitted(DELETE_REQUEST, requestId)
  const showToggleHiddenStepsButton = useIsPermitted(TOGGLE_REQUEST_HIDDEN_STEPS, requestId)

  const menuAnchorEl = useRef<HTMLButtonElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)

  const openMenu = () => setMenuOpen(true)
  const closeMenu = () => setMenuOpen(false)

  const isRequestCompleted = request?.rawData?.completedAt != null
  const isRequestRejected = request?.rawData?.state === 'REJECTED'
  const isRequestPaused = request?.rawData?.state === 'PAUSED'

  const requestType = request?.rawData.type

  // State and request-type conditions stay as they are; the gate swaps only the who-may-do-this
  // half onto the permissions service once the organisation is on it.
  const showRejectUnrejectButton =
    request != null && !isRequestCompleted && requestType !== RequestTypeEnum.SUB_REQUEST && canReject
  const showReopenButton =
    request?.rawData.completedAt != null && requestType !== RequestTypeEnum.SUB_REQUEST && canManageWorkflow
  const showResumeButton = isRequestPaused && requestType !== RequestTypeEnum.SUB_REQUEST && canPause
  const showPauseButton =
    request?.rawData.state === null && !isRequestCompleted && requestType !== RequestTypeEnum.SUB_REQUEST && canPause
  const showAuditLog = !user.isSupplier
  const showDuplicateButton =
    !user.isSupplier && [RequestTypeEnum.NEW_PURCHASE, RequestTypeEnum.ACCESS_REQUEST].includes(requestType as RequestTypeEnum)

  return (
    <>
      <Anchor buttonRef={menuAnchorEl} openMenu={openMenu} />
      <Menu
        open={menuOpen}
        anchorEl={menuAnchorEl.current}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        onClose={closeMenu}
        keepMounted // Children contain modals that should remain mounted when the menu is closed
        slotProps={{ paper: { sx: { mt: 2 } } }}
      >
        {showPauseButton && <PauseMenuItem requestId={requestId} onClose={closeMenu} />}
        {showResumeButton && <ResumeMenuItem requestId={requestId} onClose={closeMenu} />}
        {showRejectUnrejectButton && (
          <RejectMenuItem
            onReject={onRejectRequest}
            onUnreject={onUnrejectRequest}
            rejectMode={!isRequestRejected}
            onClose={closeMenu}
          />
        )}
        {showReopenButton && <ReopenMenuItem requestId={requestId} onClose={closeMenu} />}
        {showDuplicateButton && (
          <DuplicateRequestMenuItem requestId={requestId} requestName={request?.rawData.name ?? undefined} onClose={closeMenu} />
        )}
        {showAuditLog && <AuditLogMenuItem requestId={requestId} />}
        <MuteMenuItem requestId={requestId} onClose={closeMenu} />
        {showToggleHiddenStepsButton && (
          <ToggleHiddenStepsMenuItem
            showHiddenSteps={showHiddenSteps}
            onToggleHiddenSteps={toggleHiddenSteps}
            onClose={closeMenu}
          />
        )}
        <ConfidentialMenuItem requestId={requestId} isConfidential={request?.rawData.isConfidential} onClose={closeMenu} />
        {/* Delete button should always be at the bottom */}
        {showDeleteButton && (
          <DeleteMenuItem
            onDelete={onDeleteRequest}
            isRenewal={requestType === RequestTypeEnum.RENEWAL}
            isPaused={isRequestPaused}
            onClose={closeMenu}
          />
        )}
      </Menu>
    </>
  )
}
